/* eslint-disable react/require-default-props */
import { SmlsListItem } from '@smiles/smiles-ui-kit-react';
import React, { useState, useCallback, useEffect, useRef } from 'react';
import { Dropdown, Input } from 'reactstrap';
import { mapToCssModules } from 'utils/className';

interface Action {
  /**
   * Option prefix id.
   * Ex.: if use opt_country then the id is opt_country_1
   */
  id?: string;
  /**
   * Icon element
   */
  icon?: JSX.Element;
  /**
   * Label of the action
   */
  label?: string;
  /**
   * Name of the action
   */
  name: string;
}

export interface IDropDown {
  /**
   * Element id
   */
  id?: string;
  /**
   * Input id
   */
  idInput?: string;
  /**
   * Label text
   */
  textLabel?: string;
  /**
   * Label text
   */
  label?: string | JSX.Element;
  /**
   * Text only in mobile
   */
  textMobile?: string;
  /**
   * Error message
   */
  errorMessage: string;
  /**
   * Force Error message for input validation
   */
  forceError?: boolean;
  /**
   * List of actions
   */
  actions: Array<Action>;
  /**
   * Default value
   */
  defaultValue?: string;
  /**
   * Input placeholder
   */
  placeholder: string;
  /**
   * is select with search
   */
  isSelectSearch?: boolean;
  /**
   * If component is disabled
   */
  disabled?: boolean;
  /**
   * Value
   */
  value?: string;
  /**
   * On change callback
   */
  onChange?: (event: React.ChangeEvent<HTMLInputElement>) => void;
  /**
   * On focus event
   */
  onFocus?: () => void;
  /**
   * On blur event
   */
  onBlur?: () => void;
  /**
   * On click callback
   */
  onClick?: (name: string) => void;
  /**
   * On valid input
   */
  onValid?: (isValid: boolean) => void;
  /**
   * Open when typing
   */
  openWhenTyping?: boolean;
}

export const CustomDropdown: React.FC<IDropDown> = ({
  id,
  idInput,
  textLabel,
  label = textLabel,
  defaultValue = '',
  textMobile = 'Digite seu país',
  errorMessage = 'Error message',
  actions = [],
  placeholder,
  disabled = false,
  forceError = false,
  value = '',
  onChange = () => undefined,
  onClick = () => undefined,
  onValid = () => undefined,
  onFocus,
  onBlur,
  openWhenTyping = false,
  isSelectSearch = true,
}) => {
  const [dropdownOpen, setDropdownOpen] = useState<boolean>(false);
  const [isTyping, setIsTyping] = useState<boolean>(false);
  const [isFilled, setIsFilled] = useState<boolean>(false);
  const [innerValue, setInnerValue] = useState<string>(defaultValue);
  const [searchValue, setSearchValue] = useState<string>(defaultValue);
  const [isFocused, setIsFocused] = useState<boolean>(false);
  const [isValidInput, setIsValidInput] = useState<boolean>(true);
  const thisRef = useRef<HTMLUListElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const toggle = () => setDropdownOpen((prevState) => !prevState);
  const openFilter = () => setDropdownOpen(true);
  const placeholderChange = () => {
    setSearchValue(innerValue);
    setInnerValue('');
  };
  const enableOutline = useCallback(() => {
    if (onFocus) {
      onFocus();
    }

    setIsFocused(true);
  }, []);
  const disableOutline = useCallback(() => {
    if (onBlur) {
      onBlur();
    }
    setIsFocused(false);
  }, []);

  const [isDesktop, setIsDesktop] = useState<boolean>(false);

  const handleDebouncedValue = () => {
    const hasValue = innerValue.length > 0;
    const hasCorrespondentAction = hasValue ? actions.some((action) => action.name === innerValue || disabled) : true;
    setIsTyping(false);
    setIsFilled(hasValue);
    setIsValidInput(hasCorrespondentAction);
    onValid(hasCorrespondentAction);
    if (openWhenTyping) {
      onClick(hasCorrespondentAction ? innerValue : '');
      setDropdownOpen(!hasCorrespondentAction);
    }
  };

  const verifyIfIsDesktop = useCallback((width: number, height: number) => {
    if (width < 600 || height < 500) {
      setIsDesktop(false);
    } else {
      setIsDesktop(true);
    }
  }, []);

  useEffect(() => {
    verifyIfIsDesktop(window.innerWidth, window.innerHeight);
    window.addEventListener('resize', () => {
      verifyIfIsDesktop(window.innerWidth, window.innerHeight);
    });
  }, [verifyIfIsDesktop]);

  useEffect(() => {
    const debounceTimeout = setTimeout(handleDebouncedValue, 500);
    return () => {
      clearTimeout(debounceTimeout);
    };
  }, [innerValue]);

  useEffect(() => {
    if (innerValue !== value) {
      setInnerValue(value);
    }
  }, [value]);

  useEffect(() => {
    if (isTyping && openWhenTyping) {
      setDropdownOpen(true);
    }
  }, [isTyping]);

  const handleOnChange = useCallback((event) => {
    const text = event.target.value;
    setIsTyping(true);
    setInnerValue(text);
    onChange(event);
  }, []);

  const handleOnKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'ArrowDown' && thisRef.current) {
      const next = event.currentTarget.parentElement?.nextElementSibling?.children[0] as HTMLElement | null;
      if (next) {
        next.focus();
      }
    }

    if (event.key === 'ArrowUp' && thisRef.current) {
      const next = event.currentTarget.parentElement?.previousElementSibling?.children[0] as HTMLElement | null;
      if (next) {
        next.focus();
      } else {
        const inp = inputRef.current?.children[0] as HTMLElement | null;
        if (inp) {
          inp.focus();
        }
      }
    }
  };

  const handleInputKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'ArrowDown' && thisRef.current) {
      const next = thisRef.current.children[0].children[0] as HTMLElement | null;
      if (next) {
        next.focus();
      }
    }
    if (event.key === 'Escape' && dropdownOpen) {
      toggle();
    }
  };

  return (
    <div
      className={mapToCssModules([
        !isDesktop && dropdownOpen ? 'dropdown-mobile' : 'input-group',
        disabled ? 'disabled' : '',
        isTyping ? 'typing' : '',
        isFilled && isDesktop ? 'filled' : '',
        isFocused ? 'input-outline' : '',
        !isValidInput || (forceError && !dropdownOpen) ? 'error' : '',
      ])}
    >
      {label &&
        (isDesktop || (!isDesktop && !dropdownOpen)) &&
        (typeof label === 'string' ? <label htmlFor={idInput}>{label}</label> : label)}
      <Dropdown id={id} isOpen={dropdownOpen} toggle={toggle}>
        <div className="dropdown-toggle" ref={inputRef}>
          {isSelectSearch ? (
            <Input
              id={idInput}
              value={innerValue}
              disabled={disabled}
              onChange={handleOnChange}
              placeholder={searchValue || placeholder}
              onFocus={enableOutline}
              onBlur={disableOutline}
              onKeyDown={handleInputKeyDown}
              onClick={() => {
                openFilter();
                placeholderChange();
              }}
              autocomplete="off"
            />
          ) : (
            <Input
              style={{ cursor: 'pointer' }}
              id={idInput}
              value={innerValue}
              disabled={disabled}
              placeholder={searchValue || placeholder}
              onFocus={enableOutline}
              onBlur={disableOutline}
              onKeyDown={handleInputKeyDown}
              readOnly
              onClick={() => {
                openFilter();
              }}
              autocomplete="off"
            />
          )}
          <i
            onClick={() => {
              if (!disabled) {
                toggle();
                const input = inputRef.current?.children[0] as HTMLInputElement | null;

                if (input) {
                  input.focus();
                }
              }
            }}
            className="material-icons notranslate"
          >
            {!isDesktop && dropdownOpen ? 'close' : 'expand_more'}
          </i>
        </div>
        <ul className="dropdown-menu" ref={thisRef}>
          {isSelectSearch
            ? actions
                .filter((action) =>
                  isFilled
                    ? action.name.toLocaleUpperCase().includes(innerValue.toLocaleUpperCase()) ||
                      (action.label && action.label.toLocaleUpperCase().includes(innerValue.toLocaleUpperCase()))
                    : true,
                )
                .map(({ id: optId, icon, label: actionLabel, name }) => (
                  <>
                    {isDesktop ? (
                      <SmlsListItem
                        id={optId}
                        item={name}
                        label={actionLabel}
                        icon={icon}
                        key={name}
                        onClick={() => {
                          onClick(name);
                          setInnerValue(name);
                          setDropdownOpen(false);
                        }}
                        onKeyDown={handleOnKeyDown}
                      />
                    ) : (
                      <SmlsListItem
                        id={`opt_${name}`}
                        item={name}
                        key={name}
                        onClick={() => {
                          onClick(name);
                          setInnerValue(name);
                          setDropdownOpen(false);
                        }}
                        onKeyDown={handleOnKeyDown}
                      />
                    )}
                  </>
                ))
            : actions.map(({ id: optId, icon, label: actionLabel, name }) => {
                return (
                  <>
                    {isDesktop ? (
                      <SmlsListItem
                        id={optId}
                        item={name}
                        label={actionLabel}
                        icon={icon}
                        key={name}
                        onClick={() => {
                          onClick(name);
                          setInnerValue(name);
                          setDropdownOpen(false);
                        }}
                        onKeyDown={handleOnKeyDown}
                      />
                    ) : (
                      <SmlsListItem
                        id={`opt_${name}`}
                        item={name}
                        key={name}
                        onClick={() => {
                          onClick(name);
                          setInnerValue(name);
                          setDropdownOpen(false);
                        }}
                        onKeyDown={handleOnKeyDown}
                      />
                    )}
                  </>
                );
              })}
        </ul>
      </Dropdown>
      {isDesktop && (
        <div className="input-error">
          {!isValidInput ||
            (forceError && (
              <>
                <i className="material-icons notranslate">&#xe001;</i>
                <label htmlFor="input-text">{errorMessage}</label>
              </>
            ))}
        </div>
      )}
    </div>
  );
};
